import { forwardRef } from "react";
import { cn } from "@/lib/utils";

export interface TimelineItemType {
	key?: string;
	color?: "blue" | "red" | "green" | "gray" | string;
	dot?: React.ReactNode;
	label?: React.ReactNode;
	children: React.ReactNode;
}

export interface TimelineProps extends React.HTMLAttributes<HTMLDivElement> {
	items?: TimelineItemType[];
	mode?: "left" | "right" | "alternate";
	pending?: React.ReactNode;
	pendingDot?: React.ReactNode;
	reverse?: boolean;
}

const Timeline = forwardRef<HTMLDivElement, TimelineProps>(
	(
		{
			className,
			items = [],
			mode = "left",
			pending,
			pendingDot,
			reverse = false,
			children: _children,
			...props
		},
		ref,
	) => {
		const colorMap: Record<string, string> = {
			blue: "bg-primary-500",
			red: "bg-error-500",
			green: "bg-success-500",
			gray: "bg-gray-400",
		};

		const allItems = [...items];
		if (pending) {
			allItems.push({
				key: "pending",
				children: pending,
				dot: pendingDot,
				color: "gray",
			});
		}

		const displayItems = reverse ? [...allItems].reverse() : allItems;

		return (
			<div ref={ref} className={cn("relative", className)} {...props}>
				{displayItems.map((item, index) => {
					const isLast = index === displayItems.length - 1;
					const isRight = mode === "right" || (mode === "alternate" && index % 2 === 1);

					const dotColor = item.color ? colorMap[item.color] || item.color : "bg-primary-500";

					return (
						<div
							key={item.key || index}
							className={cn(
								"relative flex pb-6",
								isRight && "flex-row-reverse",
								mode === "alternate" && "justify-center",
							)}
						>
							{/* Line */}
							{!isLast && (
								<div
									className={cn(
										"absolute top-3 w-0.5 bg-gray-200 dark:bg-gray-700",
										mode === "left" && "left-1.5 h-full",
										mode === "right" && "right-1.5 h-full",
										mode === "alternate" && "left-1/2 -translate-x-1/2 h-full",
									)}
								/>
							)}

							{/* Dot */}
							<div
								className={cn(
									"relative z-10 flex-shrink-0",
									mode === "left" && "mr-4",
									mode === "right" && "ml-4",
									mode === "alternate" && (isRight ? "ml-4" : "mr-4"),
								)}
							>
								{item.dot ? (
									<div className="flex items-center justify-center">{item.dot}</div>
								) : (
									<div
										className={cn(
											"w-3 h-3 rounded-full border-2 border-white dark:border-gray-800",
											dotColor.startsWith("bg-") ? dotColor : "",
										)}
										style={!dotColor.startsWith("bg-") ? { backgroundColor: dotColor } : undefined}
									/>
								)}
							</div>

							{/* Content */}
							<div
								className={cn(
									"flex-1 min-w-0",
									mode === "alternate" && "w-[calc(50%-24px)]",
									isRight && mode === "alternate" && "text-right",
								)}
							>
								{item.label && (
									<div className="text-sm text-gray-500 dark:text-gray-400 mb-1">{item.label}</div>
								)}
								<div className="text-gray-700 dark:text-gray-200">{item.children}</div>
							</div>

							{/* Spacer for alternate mode */}
							{mode === "alternate" && <div className="w-[calc(50%-24px)]" />}
						</div>
					);
				})}
			</div>
		);
	},
);

Timeline.displayName = "Timeline";

// Timeline.Item for legacy support
const TimelineItem = forwardRef<HTMLDivElement, TimelineItemType & { className?: string }>(
	({ className, children }, ref) => {
		return (
			<div ref={ref} className={className}>
				{children}
			</div>
		);
	},
);

TimelineItem.displayName = "TimelineItem";

export { Timeline, TimelineItem };
